/**
 * Authenticated route composition for the clinical-trials dashboard.
 */

import {
  AuthWrapper,
  RbacRoleId,
  SettingsPage,
} from "@risalabs_frontend_org/auth-wrapper";
import "@risalabs_frontend_org/auth-wrapper/styles.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LandingRedirect, SuperAdminRoute } from "./components/SuperAdminRoute";
import { ClinicalTrialsAdminRoute } from "./components/ClinicalTrialsAdminRoute";
import { FilterValuesProvider } from "./contexts/filterValuesContext";
import { TablesContextProvider } from "./contexts/tablesContextProvider";
import { UserProvider } from "./contexts/UserContext";
import { ApiBaseUrls } from "./enums/apiUrls";
import { CohortScreeningPage } from "./pages/CohortScreeningPage";
import { HomeShell } from "./pages/HomeShell";
import { NotificationsPage } from "./pages/NotificationsPage";
import { DocumentSummaryPage } from "./pages/DocumentSummaryPage";
import { ManualSummarizationPage } from "./pages/ManualSummarizationPage";
import { NotesPage } from "./pages/NotesPage";
import { ProtocolsPage } from "./pages/ProtocolsPage";
import { ProtocolDetailPage } from "./pages/ProtocolDetailPage";
import { EligibilityWorklistRoute } from "./pages/EligibilityWorklistRoute";
import SinglePatientEligibilityDetails from "./pages/SinglePatientEligibilityDetails";
import { ScreeningPage } from "./pages/ScreeningPage";

import * as firebaseDevConfig from "./api/firebase/config-dev.json";
import * as firebaseProdConfig from "./api/firebase/config-prod.json";

const isProduction = process.env.REACT_APP_ENV === "production";
const firebaseConfig = isProduction ? firebaseProdConfig : firebaseDevConfig;

/**
 * Root application component. Wraps every route in `AuthWrapper`, which owns the
 * full RISA auth flow used in dev/prod — Firebase magic-link login plus the safe
 * TOTP 2FA challenge (enrollment/verification against `auth/verify-totp`). No CT
 * dashboard code implements 2FA; passing the auth + backend URLs is sufficient
 * for the shared package to resolve the 2FA endpoints.
 *
 * @returns The routed, auth-gated application tree
 */
const App = () => {
  return (
    <BrowserRouter>
      <AuthWrapper
        config={{
          firebaseConfig,
          apiUrls: {
            authUrl: ApiBaseUrls.AUTH_URL,
            // auth/token + auth/verify-totp (TOTP 2FA) live on backend-service.
            backendServiceUrl: ApiBaseUrls.BACKEND_SERVICE,
          },
          bearerAuth: {
            username: process.env.REACT_APP_USERNAME,
            password: process.env.REACT_APP_PASSWORD,
          },
          authRedirectPath: "/auth",
          facilityRecoverySettingsPath: "/settings",
          version: process.env.REACT_APP_VERSION,
        }}
      >
        <UserProvider>
          <TablesContextProvider>
            <FilterValuesProvider>
              <Routes>
                <Route path="/" element={<HomeShell />}>
                  <Route index element={<LandingRedirect />} />
                  <Route
                    path="notifications"
                    element={
                      <SuperAdminRoute>
                        <NotificationsPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="eligibility"
                    element={<EligibilityWorklistRoute />}
                  >
                    <Route
                      path=":trialId/:mrn"
                      element={<SinglePatientEligibilityDetails />}
                    />
                  </Route>
                  <Route
                    path="notes"
                    element={
                      <ClinicalTrialsAdminRoute>
                        <NotesPage />
                      </ClinicalTrialsAdminRoute>
                    }
                  />
                  <Route
                    path="manual-summarization"
                    element={
                      <SuperAdminRoute>
                        <ManualSummarizationPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="cohort-screening"
                    element={
                      <SuperAdminRoute>
                        <CohortScreeningPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="protocols"
                    element={
                      <SuperAdminRoute>
                        <ProtocolsPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="screening"
                    element={
                      <SuperAdminRoute>
                        <ScreeningPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="protocols/:trialId"
                    element={
                      <SuperAdminRoute>
                        <ProtocolDetailPage />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="patients/:mrn/summary"
                    element={
                      <ClinicalTrialsAdminRoute>
                        <DocumentSummaryPage />
                      </ClinicalTrialsAdminRoute>
                    }
                  />
                  <Route
                    path="settings/*"
                    element={
                      <div className="h-full min-h-0 overflow-hidden bg-white">
                        <SettingsPage
                          roleOptions={[
                            {
                              value: RbacRoleId.CLINICAL_TRIALS_USER,
                              label: "Clinical Trials User",
                            },
                            {
                              value: RbacRoleId.CLINICAL_TRIALS_ADMIN,
                              label: "Clinical Trials Admin",
                            },
                          ]}
                        />
                      </div>
                    }
                  />
                </Route>
                <Route path="*" element={<LandingRedirect />} />
              </Routes>
            </FilterValuesProvider>
          </TablesContextProvider>
        </UserProvider>
      </AuthWrapper>
    </BrowserRouter>
  );
};

export default App;
